import { contains, defaultTo, equals, find, isEmpty, join, map, pipe, propEq, replace, split, values } from 'ramda';
import { IDecodedPublicMediaFolderId, TDecodedPublicMediaFolderIdList } from './withPublicMedia.types';
import { isMediaFromWix } from '../../media-manager-sdk/sources';
import { Source } from '../../../constants/Source';
import { Orientation } from '../../../constants/Orientation';
import { MediaManagerSDKFolder } from '../../media-manager-sdk';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { ROOT_TITLE_MAP } from './withPublicMedia.constants';
import { Color } from '../../../constants/Color';
import { Pricing } from '../../../constants/Pricing';

export const getColorOverride = (color: Color): Color => {
  if (equals(color, Color.Any)) {
    return Color.None;
  }

  return color;
};

export const decodePublicMediaFolderId = (id: string = ''): IDecodedPublicMediaFolderId => {
  const [categoryId, subCategoryId, orientation = Orientation.All, color = Color.Any, pricing = Pricing.All] = pipe<
    string,
    string,
    string[]
  >(
    defaultTo(''),
    split('.'),
  )(id) as TDecodedPublicMediaFolderIdList;

  return {
    categoryId,
    subCategoryId,
    orientation: contains(orientation, values(Orientation)) ? orientation : Orientation.All,
    color,
    pricing,
  };
};

export const encodeBiCategory = (id: string, categories, subCategoryId?: string): string =>
  JSON.stringify({
    id: subCategoryId || id,
    title: subCategoryId
      ? find<{ name: string; id: string }>(propEq('id', subCategoryId), categories[id].subFolders).name
      : categories[id].name,
  });

export const encodePublicMediaFolderId = ({
  categoryId,
  subCategoryId,
  orientation = Orientation.All,
  color = Color.Any,
  pricing = Pricing.All,
}: Partial<IDecodedPublicMediaFolderId>): string => join('.', [categoryId, subCategoryId, orientation, color, pricing]);

export const convertPublicMediaPathToOutputPath = pipe<string, string, string>(defaultTo(''), replace(/\./g, '/'));

export const deconvertOutputPathToPublicMediaPath = (path: string): string => {
  if (isEmpty(path)) {
    return '';
  }

  const [source, ...folderIdChunks] = split('/', path);

  if (!isMediaFromWix(source as Source)) {
    throw new Error('Non Public path cannot be parsed with this method');
  }

  return `${source}/${join('.', folderIdChunks)}`;
};

export const getColumnsCount: (itemsCount: number, maxNumberOfItemsInColumn: number) => number = (
  itemsCount,
  maxNumberOfItemsInColumn,
) => {
  const result = itemsCount / maxNumberOfItemsInColumn;
  const columnsCount = Math.round(result);

  return columnsCount >= result ? columnsCount : columnsCount + 1;
};

export const getPossiblePublicFolder = (folderId: string, folders: MediaManagerSDKFolder[]): MediaManagerSDKFolder =>
  find<MediaManagerSDKFolder>(propEq('id', folderId), folders);

export const getTitleByPublicMediaRoot = (publicMediaRoot: WixMediaRoot, defaultTitle: string): string =>
  ROOT_TITLE_MAP[publicMediaRoot] || defaultTitle;

export const getCategoriesData = (folderId, categories) => {
  const decodedFolderId = decodePublicMediaFolderId(folderId);

  return map(
    (categoryId) =>
      categoryId === decodedFolderId.categoryId
        ? { mediaType: categories[categoryId].mediaType, id: decodedFolderId.subCategoryId }
        : {
            mediaType: categories[categoryId].mediaType,
            id: categories[categoryId].subFolders[0].id,
          },
    Object.keys(categories),
  );
};
